import React, { useState } from "react";
import ShowPasswordSVG from "../icons/ShowPasswordSVG";
import HidePasswordSVG from "../icons/HidePasswordSVG";
import "./Input.css";
import { ErrorMessage } from "./InputError";

const Input = ({
  placeHolder,
  color,
  Ref,
  onChange,
  onClick,
  readOnly,
  value,
  defaultValue,
  formatValue,
  icon,
  name,
  label,
  style,
  inputStyle,
  labelStyle,
  hint,
  InlineComp = () => null,
  className,
  password = false,
  multiLine = false,
  underline = false,
  disabled = false,
  onKeyDown,
  onPaste,
  onBlur,
  autoComplete,
  type = "text",
  inputMode,
  autoCapitalize,
  autoCorrect,
  copy,
  onHintClick,
  error,
  hintRightOffset = "0rem",
}) => {
  const [hidePassword, setHidePassword] = useState(password);
  const [isFocused, setIsFocused] = useState(false);
  const [hasValue, setHasValue] = useState(
    value || defaultValue ? true : false
  );
  const previousValue = React.useRef(value || defaultValue || "");
  const [hintOffset, setHintOffset] = useState(0);
  const inputRef = React.useRef(null);

  const FONT_SIZE = 0.8;
  const INPUT_LINE_HEIGHT = "1.25rem";
  const INPUT_FONT_FAMILY = "Primary-Light";
  const INPUT_PADDING = 0; // in rem (from px-kit-24 class)

  const togglePasswordShow = () => {
    setHidePassword((prev) => !prev);
  };

  const handleChange = (event) => {
    if (formatValue) {
      event.target.value = formatValue(
        event.target.value,
        previousValue.current
      );
    }

    setHasValue(event.target.value ? true : false);
    onChange && onChange(event);

    previousValue.current = event.target.value;
  };

  // Handle Enter key to dismiss keyboard
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !multiLine) {
      e.preventDefault();
      // Blur the input to dismiss the keyboard
      if (Ref?.current) {
        Ref.current.blur();
      } else if (inputRef?.current) {
        const actualInput = inputRef.current.querySelector('input, textarea');
        if (actualInput) {
          actualInput.blur();
        }
      }
    }
    // Call custom onKeyDown if provided
    if (onKeyDown) {
      onKeyDown(e);
    }
  };

  const focusInputFromWrapper = (event) => {
    onClick && onClick(event);
    if (disabled || event.defaultPrevented) return;

    const target = event.target;
    const isInteractiveChild = target.closest?.("button, a, [role='button']");
    if (isInteractiveChild) return;

    const field =
      Ref?.current || inputRef.current?.querySelector("input, textarea");
    field?.focus?.();
  };

  React.useEffect(() => {
    if (!hint) return;

    const calculateHintOffset = () => {
      const hintSpan = document.createElement('span');
      hintSpan.style.position = 'absolute';
      hintSpan.style.visibility = 'hidden';
      hintSpan.style.whiteSpace = 'pre';
      hintSpan.style.fontSize = `${FONT_SIZE}rem`;
      hintSpan.style.fontFamily = INPUT_FONT_FAMILY;
      hintSpan.style.lineHeight = INPUT_LINE_HEIGHT;
      hintSpan.textContent = value || placeHolder || "";
      document.body.appendChild(hintSpan);
      const hintWidth = hintSpan.offsetWidth;
      document.body.removeChild(hintSpan);
      setHintOffset(hintWidth);
    };

    // Calculate initial offset
    calculateHintOffset();

    // Set up ResizeObserver
    const resizeObserver = new ResizeObserver(calculateHintOffset);
    if (inputRef.current) {
      resizeObserver.observe(inputRef.current);
    }

    // Clean up
    return () => {
      resizeObserver.disconnect();
    };
  }, [value, placeHolder, hint, FONT_SIZE]);

  return (
    <div className="flex flex-col w-full">
      {label && (
        <label
          className="ui-label mb-kit-8"
          style={labelStyle}
          htmlFor={name}
        >
          {label}
          {copy && <div className="mt-kit-8">{copy}</div>}
        </label>
      )}
      <div
        className={
          `ui-input-wrapper mt-kit-8 flex gap-kit-8 relative justify-center items-center px-kit-16 py-kit-4 rounded-[var(--inyc-input-border-radius)] transition-all duration-200  ${
            underline
              ? `border-b-[2px] duration-300 ${
                  hasValue || isFocused
                    ? "border-b-black"
                    : "border-b-divider"
                }`
              : ""
          } ` + className
        }
        style={style}
        onClick={focusInputFromWrapper}
      >
        {icon && <div className="ui-input-icon" style={{ height: '1.5rem', width: '1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{icon}</div>}
        {disabled && (
          <div className="absolute w-full h-full bg-black opacity-[0.01] z-10 rounded-kit-lg pointer-events-all"></div>
        )}
        <div className="relative flex-1 overflow-hidden" ref={inputRef}>
          {hint && (value || placeHolder) && (
            <span
              className={`absolute text-[#adacac] whitespace-pre overflow-hidden text-ellipsis ${
                onHintClick ? "cursor-pointer" : "pointer-events-none"
              }`}
              style={{
                left: `calc(${hintOffset}px + 1.2rem)`,
                top: "50%",
                transform: "translateY(-50%)",
                fontSize: `${FONT_SIZE}rem`,
                fontFamily: INPUT_FONT_FAMILY,
                lineHeight: INPUT_LINE_HEIGHT,
                maxWidth: `calc(100% - ${hintOffset}px - 1.5rem - ${hintRightOffset})`,
              }}
              onClick={onHintClick}
            >
              {value?.endsWith(" ") ? hint?.trimStart() : hint}
            </span>
          )}

          {multiLine ? (
            <textarea
              aria-invalid={error ? true : undefined}
              onKeyDown={onKeyDown}
              onPaste={onPaste}
              readOnly={readOnly}
              style={{
                padding: 'var(--inyc-space-4) var(--inyc-space-10)',
                fontSize: 'var(--inyc-text-base)',
                width: '100%',
                ...(color ? { color: color } : {}),
                ...(inputStyle || {}),
                minHeight: inputStyle?.minHeight || "160px",
                resize: inputStyle?.resize || "vertical",
              }}
              className="ui-input"
              type={hidePassword ? "password" : type}
              placeholder={placeHolder}
              ref={Ref}
              defaultValue={defaultValue}
              onChange={handleChange}
              onFocus={() => {
                setIsFocused(true);
              }}
              onBlur={(e) => {
                setIsFocused(false);
                onBlur && onBlur(e);
              }}
              value={value}
              name={name}
              id={name}
            />
          ) : (
            <input
              aria-invalid={error ? true : undefined}
              onKeyDown={handleKeyDown}
              onPaste={onPaste}
              readOnly={readOnly}
              style={{ padding: 'var(--inyc-space-4) var(--inyc-space-10)', fontSize: 'var(--inyc-text-base)', lineHeight: INPUT_LINE_HEIGHT, width: '100%', ...(color ? { color: color } : {}), ...(inputStyle || {}) }}
              className="ui-input"
              type={hidePassword ? "password" : "text"}
              placeholder={placeHolder}
              ref={Ref}
              defaultValue={defaultValue}
              onChange={handleChange}
              onFocus={() => {
                setIsFocused(true);
              }}
              onBlur={(e) => {
                setIsFocused(false);
                onBlur && onBlur(e);
              }}
              value={value}
              name={name}
              id={name}
              autoComplete={autoComplete}
              autoCapitalize={autoCapitalize || (type === "email" ? "none" : undefined)}
              autoCorrect={autoCorrect || (type === "email" ? "off" : undefined)}
              // iOS keyboard dismissal attributes
              enterKeyHint="done"
              inputMode={inputMode || (type === "email" ? "email" : "text")}
            />
          )}
        </div>
        {/* {hint && <div className="text-kit-md rounded-kit-xs px-kit-8 text-[--inyc-primary-text-color-light] whitespace-nowrap py-kit-4">{hint}</div>} */}
        {<InlineComp />}
        {password && (
          <div
            className="ui-input-icon"
            onClick={togglePasswordShow}
            style={{ cursor: "pointer", height: '1.5rem', width: '1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
          >
            {hidePassword ? <ShowPasswordSVG /> : <HidePasswordSVG />}
          </div>
        )}
      </div>
      {typeof error === "string" && error && <ErrorMessage>{error}</ErrorMessage>}
    </div>
  );
};

export default React.memo(Input);
